import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { getDashboardSummary, checkMonitoringHealth } from '../../../services/monitoring.service';

/**
 * Sidebar Status Indicator Component
 * Displays service monitoring health status in the sidebar
 *
 * Auto-refresh intervals (context-aware, only if monitoring is enabled):
 * - On monitoring dashboard page: Every 30 seconds
 * - On other pages: Every 30 minutes
 *
 * Status Colors:
 * - Green: Healthy (>= 95% success rate)
 * - Yellow: Warning (80-94% success rate)
 * - Red: Critical (< 80% success rate)
 * - Gray: Unavailable/Disabled
 *
 * Clicking navigates to the full Service Monitoring Dashboard
 */
class SidebarStatusIndicator extends Component {
  constructor(props) {
    super(props);
    this.state = {
      summary: null,
      loading: true,
      error: null,
      monitoringDisabled: false,
    };
    this.refreshInterval = null;

    // Polling intervals based on page context
    this.MONITORING_PAGE_INTERVAL = 30000;  // 30 seconds on monitoring dashboard
    this.OTHER_PAGE_INTERVAL = 1800000;     // 30 minutes on other pages
  }

  componentDidMount() {
    // Don't poll if we're not authenticated (e.g., on login page)
    if (!this.isAuthenticated()) {
      this.setState({ loading: false, monitoringDisabled: true });
      return;
    }

    // Check monitoring health before starting polling
    this.checkMonitoringHealthStatus();
  }

  componentDidUpdate(prevProps) {
    // If route changed, restart polling with appropriate interval
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.restartPolling();
    }
  }

  componentWillUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  /**
   * Check if user is authenticated
   * Prevents API calls on login page or when not logged in
   */
  isAuthenticated = () => {
    // Check if we're on login page
    if (this.props.location.pathname === '/login' ||
        this.props.location.pathname === '/auth/login') {
      return false;
    }

    // Check if user has a valid auth token (adjust based on your auth implementation)
    const token = localStorage.getItem('authToken') ||
                  localStorage.getItem('token') ||
                  sessionStorage.getItem('authToken');

    return !!token;
  };

  /**
   * Get the appropriate polling interval based on current route
   */
  getPollingInterval = () => {
    const isMonitoringPage = this.props.location.pathname === '/admin/service-monitoring';
    return isMonitoringPage ? this.MONITORING_PAGE_INTERVAL : this.OTHER_PAGE_INTERVAL;
  };

  /**
   * Restart polling with the appropriate interval for current page
   */
  restartPolling = () => {
    // Clear existing interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }

    // Start new interval with updated frequency
    const interval = this.getPollingInterval();
    this.refreshInterval = setInterval(() => {
      this.fetchSummary(true); // silent refresh
    }, interval);

    //console.log(`Monitoring sidebar polling interval updated: ${interval / 1000}s`);
  };

  /**
   * Check if monitoring module is enabled before starting polling
   * If disabled, show gray indicator and don't poll
   */
  checkMonitoringHealthStatus = async () => {
    try {
      const healthResponse = await checkMonitoringHealth();

      // Check both PascalCase and camelCase for compatibility
      const monitoringEnabled = ('monitoringEnabled' in healthResponse)
        ? healthResponse.monitoringEnabled
        : healthResponse.MonitoringEnabled;

      if (!monitoringEnabled) {
        // Monitoring is disabled - show gray indicator, don't poll
        this.setState({
          monitoringDisabled: true,
          loading: false,
          summary: {
            totalServices: 0,
            runningServices: 0,
            healthStatus: 'Unavailable'
          }
        });
        console.warn('Service monitoring disabled - SidebarStatusIndicator will not poll');
        return;
      }

      // Monitoring is enabled - start polling with context-aware interval
      this.fetchSummary();
      const interval = this.getPollingInterval();
      this.refreshInterval = setInterval(() => {
        this.fetchSummary(true); // silent refresh
      }, interval);

      //console.log(`Monitoring sidebar polling started: ${interval / 1000}s interval`);

    } catch (error) {
      console.error('Failed to check monitoring health:', error);
      // If health check fails, proceed anyway for backward compatibility
      this.fetchSummary();
      const interval = this.getPollingInterval();
      this.refreshInterval = setInterval(() => {
        this.fetchSummary(true);
      }, interval);
    }
  };

  fetchSummary = async (silent = false) => {
    try {
      if (!silent) {
        this.setState({ loading: true, error: null });
      }

      const response = await getDashboardSummary();
      const summary = response.Data || response.data || response;

      this.setState({
        summary,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error fetching service monitoring summary:', error);
      this.setState({
        error: error.message || 'Failed to load status',
        loading: false,
      });
    }
  };

  getStatusColor = () => {
    const { summary, monitoringDisabled } = this.state;

    // Show gray if monitoring is disabled
    if (monitoringDisabled || !summary) {
      return '#6c757d'; // gray
    }


    const healthStatus = summary.overallHealth;

    if (!healthStatus) {
      return '#6c757d'; // gray
    }

    switch (healthStatus) {
      case 'Healthy':
        return '#28a745'; // green
      case 'Warning':
        return '#ffc107'; // yellow
      case 'Critical':
        return '#dc3545'; // red
      default:
        return '#6c757d'; // gray
    }
  };

  handleClick = () => {
    this.props.history.push('/admin/service-monitoring');
  };

  render() {
    const { summary, loading, error, monitoringDisabled } = this.state;

    // Hide completely if there's an error
    if (error && !monitoringDisabled) {
      return null;
    }

    // Show indicator even if disabled (with gray color)
    return (
      <div
        className="sidebar-status-indicator d-flex align-items-center px-3 py-2"
        onClick={this.handleClick}
        style={{
          cursor: 'pointer',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          backgroundColor: '#2e3548',
          transition: 'background-color 0.2s ease',
        }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3d2f34'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3548'}
        title={monitoringDisabled
          ? "Service Monitoring is disabled - Click to view configuration instructions"
          : "Click to view Service Monitoring Dashboard"}
      >
        <div
          className="status-circle mr-2"
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: loading ? '#6c757d' : this.getStatusColor(),
            transition: 'background-color 0.3s ease',
          }}
        ></div>
        <div className="text-truncate" style={{ fontSize: '0.8rem', color: '#e6e7e8' }}>
          <strong>Services:</strong>{' '}
          {loading ? (
            <span>Loading...</span>
          ) : monitoringDisabled ? (
            <span>Disabled</span>
          ) : summary ? (
            <span>
              {summary.runningServices}/{summary.totalServices} Running
            </span>
          ) : (
            <span>N/A</span>
          )}
        </div>
      </div>
    );
  }
}

export default withRouter(SidebarStatusIndicator);
